import { adminApi } from "../repositories/admin";

/**
 * Tracks a SoundChart API call
 * Fire-and-forget pattern - doesn't block main functionality
 */
export const trackSoundChartApiCall = async (
  apiCall: string,
  email: string | null,
  labelName: string | null,
  isSucceeded: boolean
): Promise<void> => {
  // Don't track if email is missing
  if (!email) {
    console.warn(
      "📊 [SoundChart Tracking] Skipped - Missing email:",
      { email, labelName }
    );
    return;
  }

  // Use "No Label" as default labelName if not provided
  const effectiveLabelName = labelName || "No Label";

  try {
    const now = new Date();
    const date = now.toISOString().split("T")[0]; // "2025-01-15"
    const time = now.toTimeString().split(" ")[0]; // "14:30:25"

    const payload = {
      api_call: apiCall,
      date,
      time,
      email,
      labelName: effectiveLabelName,
      isSucceeded,
    };

    console.log("📊 [SoundChart Tracking] Sending tracking data:", payload);

    // Fire and forget - no await
    adminApi
      .trackSoundChartApi(payload)
      .then(() => {
        console.log("✅ [SoundChart Tracking] Successfully tracked API call:", apiCall);
      })
      .catch((error) => {
        // Log the error but don't break user experience
        console.error("❌ [SoundChart Tracking] Failed to track API call:", {
          apiCall,
          error: error?.response?.data || error?.message || error,
          status: error?.response?.status,
        });
      });
  } catch (error) {
    // Log the error but don't break user experience
    console.error("❌ [SoundChart Tracking] Exception in tracking:", error);
  }
};

/**
 * Extracts API endpoint from full URL
 * Example: "https://customer.api.soundcharts.com/api/v2/song/123/playlist/current/spotify" -> "/song/:uuid/playlist/current/:platform"
 */
export const extractSoundChartEndpoint = (url: string): string => {
  try {
    // Remove base URL and query params
    const urlObj = new URL(url);
    let path = urlObj.pathname;

    // Remove /api/v2, /api/v2.20, etc. prefix if present
    path = path.replace(/^\/api\/v[\d.]+/, "");

    // Replace UUIDs (long alphanumeric strings) with :uuid
    path = path.replace(
      /\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
      "/:uuid"
    );

    // Replace other IDs (numbers or alphanumeric) with :id
    path = path.replace(/\/[A-Z0-9]{10,}/gi, "/:id");

    // Replace ISRCs (format: CC-XXX-YY-NNNNN)
    path = path.replace(/\/[A-Z]{2}-[A-Z0-9]{3}-\d{2}-\d{5}/gi, "/:isrc");

    return path || "/";
  } catch (error) {
    // If URL parsing fails, return the original URL
    return url;
  }
};
